const FRESH_USER_BALANCE_OVERRIDE_TTL_MS = 60_000

interface FreshUserBalanceOverride {
	userUid: string
	issuer?: number
	currency: string
	balance: number
	expiresAt: number
}

const freshUserBalanceOverrides = new Map<string, FreshUserBalanceOverride>()

const getOverrideKey = (userUid: string, issuer?: number) =>
	`${userUid}:${issuer ?? "default"}`

const getIssuerBalance = (user: IUserSaved, issuer?: number) => {
	if (issuer === undefined) return user.team_balance
	return user.team_balances?.find(account => account.issuer === issuer)?.team_balance ?? user.team_balance
}

export const recordFreshUserBalanceOverride = (
	override: Omit<FreshUserBalanceOverride, "expiresAt">
) => {
	freshUserBalanceOverrides.set(getOverrideKey(override.userUid, override.issuer), {
		...override,
		expiresAt: Date.now() + FRESH_USER_BALANCE_OVERRIDE_TTL_MS
	})
}

export const applyFreshUserBalanceOverride = <T extends IUserSaved>(
	user: T,
	issuer?: number
): T => {
	const overrideKey = getOverrideKey(user.uid, issuer)
	const override = freshUserBalanceOverrides.get(overrideKey)
	if (!override) return user

	if (override.expiresAt <= Date.now()) {
		freshUserBalanceOverrides.delete(overrideKey)
		return user
	}

	if (Number(getIssuerBalance(user, override.issuer)) === override.balance) {
		freshUserBalanceOverrides.delete(overrideKey)
		return user
	}

	return {
		...user,
		team_balance: override.balance,
		...(override.issuer === undefined
			? {}
			: {
				team_balances: [
					...(user.team_balances ?? []).filter(account => account.issuer !== override.issuer),
					{
						issuer: override.issuer,
						currency: override.currency,
						team_balance: override.balance
					}
				]
			})
	}
}

export const clearFreshUserBalanceOverrides = () => {
	freshUserBalanceOverrides.clear()
}
